import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getClassroomData } from 'api/classroomApi';
import { createTeam } from 'api/userApi';
import { queryClient } from 'App';
import classNames from 'classnames';
import IconButton from 'components/common/IconButton';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import { useUser } from 'context/UserContext';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Card, Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Step1 from './create/Step1';
import Step2 from './create/Step2';
import Step3 from './create/Step3';
import Step4 from './create/Step4';

const CreateTeam = () => {
  const navItems = [
    {
      icon: 'list-ul',
      label: 'Regole'
    },
    {
      icon: 'info',
      label: 'Dettagli'
    },
    {
      icon: 'users',
      label: 'Giocatori'
    },
    {
      icon: 'check',
      label: 'Conferma'
    }
  ];
  const [step, setStep] = useState(1);
  useUser();

  const [shouldContinue, setShouldContinue] = useState(true);

  //TEAM DETAILS useStates
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');

  //these are used to store students and teachers data as a "dictionary" (an object) to access to each player property
  const [studentsData, setStudentsData] = useState({});
  const [teachersData, setTeachersData] = useState({});

  const {
    isSuccess,
    data: classroom,
    isError,
    error
  } = useQuery({
    queryKey: ['classroomData'],
    queryFn: getClassroomData
  });

  if (isError) {
    toast.error(error.message);
    navigate('/classe');
    return;
  }

  useEffect(() => {
    if (isSuccess) {
      const reducedStudents = {};
      classroom.students.forEach(student => {
        reducedStudents[student.code] = student;
      });
      setStudentsData(reducedStudents);

      const reducedTeachers = {};
      classroom.teachers.forEach(teacher => {
        reducedTeachers[teacher.code] = teacher;
      });
      setTeachersData(reducedTeachers);
    }
  }, [isSuccess]);

  const [students, setStudents] = useState({
    selected: [],
    totalPrice: 0
  });

  const [teacher, setTeacher] = useState({
    selected: null,
    totalPrice: 0
  });

  const handleNext = () => {
    if (shouldContinue) {
      setShouldContinue(false);
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setShouldContinue(true);
    setStep(step - 1);
  };

  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: createTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      toast.success('Hai creato la tua squadra!');
      navigate('/team');
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const handleSubmit = () => {
    const createTeamRequest = {
      name: teamName,
      description: teamDescription,
      teacher: teacher.selected.value,
      students: students.selected.map(item => item.value)
    };
    mutation.mutate(createTeamRequest);
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div>
            <h3 className="fw-light fs-4 mb-0 text-primary text-center">
              <strong>Crea la tua SQUADRA</strong>
            </h3>
            <h5 className="fw-light mt-3 text-center">
              Scegli subito i <strong>giocatori</strong> della tua squadra e dai
              il via alla <strong>competizione</strong>!
            </h5>
          </div>
        </Card.Body>
      </Card>
      <Card className="theme-wizard mb-5">
        <Card.Header className="bg-body-tertiary pb-2">
          <Nav className="justify-content-center">
            {navItems.map((item, index) => (
              <NavItem
                key={item.label}
                index={index + 1}
                step={step}
                handleNavs={() => {}}
                icon={item.icon}
                label={item.label}
              />
            ))}
          </Nav>
        </Card.Header>
        <Card.Body className="fw-normal px-md-6 py-4">
          {step === 1 && <Step1 />}
          {step === 2 && (
            <Step2
              teamName={teamName}
              setTeamName={setTeamName}
              teamDescription={teamDescription}
              setTeamDescription={setTeamDescription}
              setShouldContinue={setShouldContinue}
            />
          )}
          {step === 3 && (
            <Step3
              teamName={teamName}
              teamDescription={teamDescription}
              students={students}
              setStudents={setStudents}
              studentsData={studentsData}
              teacher={teacher}
              setTeacher={setTeacher}
              teachersData={teachersData}
              setShouldContinue={setShouldContinue}
            />
          )}
          {step === 4 && (
            <Step4
              teamName={teamName}
              teamDescription={teamDescription}
              studentsData={studentsData}
              students={students}
              teachersData={teachersData}
              teacher={teacher}
              setShouldContinue={setShouldContinue}
            />
          )}
        </Card.Body>
        <Card.Footer className="px-md-6 bg-body-tertiary d-flex">
          <IconButton
            variant="link"
            icon="chevron-left"
            iconAlign="left"
            transform="down-1 shrink-4"
            className={classNames('px-0 fw-semibold', {
              'd-none': step === 1
            })}
            onClick={handlePrev}
          >
            Indietro
          </IconButton>
          {step < 4 ? (
            <IconButton
              variant="primary"
              className="ms-auto px-5"
              type="submit"
              icon="chevron-right"
              iconAlign="right"
              transform="down-1 shrink-4"
              onClick={handleNext}
              disabled={!shouldContinue}
            >
              Avanti
            </IconButton>
          ) : (
            <LoadingButton
              text="Conferma"
              className="ms-auto px-5"
              isLoading={mutation.isPending}
              type="submit"
              action={handleSubmit}
            />
          )}
        </Card.Footer>
      </Card>
    </>
  );
};

const NavItem = ({ index, step, handleNavs, icon, label }) => {
  return (
    <Nav.Item>
      <Nav.Link
        className={classNames('fw-semibold', {
          done: index < 4 ? step > index : step > 3,
          active: step === index
        })}
        onClick={() => handleNavs(index)}
      >
        <span className="nav-item-circle-parent">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span className="d-none d-md-block mt-1 fs-10">{label}</span>
      </Nav.Link>
    </Nav.Item>
  );
};

NavItem.propTypes = {
  index: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  handleNavs: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};

export default CreateTeam;
